import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { createForm } from '@sonofabit/ng-core';
import { ManagerDetails, TenantService, UserItemDto, UserService } from '@artemis-software/wr-api';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbDialogRef } from '@nebular/theme';
import { Tracked } from '@/utils/signals/tracked';

@Component({
  standalone: false,
  selector: 'wr-property-manager-dialog',
  templateUrl: './property-manager-dialog.component.html',
  styleUrls: ['./property-manager-dialog.component.scss'],
})
export class PropertyManagerDialogComponent implements OnInit {
  readonly tenantService = inject(TenantService);
  readonly dialogRef = inject(NbDialogRef<PropertyManagerDialogComponent>);
  readonly userService = inject(UserService);
  protected readonly FormControl = FormControl;

  form?: ReturnType<typeof this.initForm>;
  selectedOption: 'user' | 'manual' = 'user';

  @Tracked()
  @Input()
  buildingTenantId?: string | null;
  @Input()
  propertyManager?: UserItemDto | ManagerDetails | null;

  buildingTenantId$ = new BehaviorSubject<string[]>([]);

  constructor(){
    effect(() => {
      if(this.buildingTenantId){
        this.buildingTenantId$.next([this.buildingTenantId]);
      }
    });
  }

  ngOnInit(): void {
    this.form = this.initForm(this.propertyManager ?? undefined);

    if (this.propertyManager) {
      if ('id' in this.propertyManager) {
        this.selectedOption = 'user';
      } else {
        this.selectedOption = 'manual';
      }
    }
  }

  initForm(initialValue: UserItemDto | ManagerDetails | undefined) {
    const isUser = initialValue && 'id' in initialValue;
    const user = isUser ? (initialValue as UserItemDto) : undefined;
    const manager = !isUser ? (initialValue as ManagerDetails) : undefined;

    return createForm(({ field }) => {
      return {
        user: field({ value: user }),
        firstName: field({
          value: user?.firstName ?? manager?.propertyManagerFirstName ?? '',
          validators: [Validators.required]
        }),
        lastName: field({
          value: user?.lastName ?? manager?.propertyManagerLastName ?? '',
          validators: [Validators.required]
        }),
        email: field({
          value: user?.email ?? manager?.propertyManagerEmail ?? '',
        }),
        phoneNumber: field({
          value: manager?.propertyManagerPhoneNumber ?? user?.phoneNumber ?? '',
        }),
        mobileNumber: field({
          value: manager?.propertyManagerMobileNumber ?? user?.mobileNumber ?? '',
        }),
      };
    });
  }

  close() {
    this.dialogRef.close();
  }

  async submit() {
    if (!this.form) return;

    if (this.selectedOption === 'user') {
      const user = this.form.get('user')?.value;
      if (user?.id) {
        const fullUser = await firstValueFrom(this.userService.findById(user.id));
        this.dialogRef.close({ propertyManager: fullUser });
      } else {
        this.close();
      }
    } else {
      this.dialogRef.close({
        embeddedPropertyManager: {
          propertyManagerFirstName: this.form.get('firstName')?.value || '',
          propertyManagerLastName: this.form.get('lastName')?.value || '',
          propertyManagerEmail: this.form.get('email')?.value || '',
          propertyManagerPhoneNumber: this.form?.get('phoneNumber')?.value || '',
          propertyManagerMobileNumber: this.form?.get('mobileNumber')?.value || '',
        } as ManagerDetails,
      });
    }
  }

}
